/**
 * My Profile Page
 *
 * Allows active users to view and edit their instructor profile
 * Contains tabs for viewing public profile and editing profile
 */

import { useState } from 'preact/hooks';
import { useAuth } from '../../hooks/useAuth';
import { ProfileEditor } from '../../components/profile/ProfileEditor';

export function MyProfile() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'view' | 'edit'>('edit');

  // Loading state - wait for user to be loaded
  if (!user) {
    return (
      <div class="flex items-center justify-center min-h-screen">
        <div class="text-center">
          <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p class="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // Check user status - only active users can edit their profile
  if (user.status !== 'active') {
    return (
      <div class="min-h-screen bg-gray-100 flex items-center justify-center">
        <div class="max-w-md bg-white rounded-lg shadow-lg p-8">
          <div class="text-center">
            <svg class="mx-auto h-16 w-16 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 class="mt-4 text-xl font-bold text-gray-900">Acceso Restringido</h2>
            <p class="mt-2 text-gray-600">
              Solo usuarios con cuenta activa pueden editar su perfil.
            </p>
            <p class="mt-2 text-sm text-gray-500">
              Tu estado actual: <span class="font-semibold">{user.status}</span>
            </p>
            <div class="mt-6">
              <a
                href="/"
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                Volver al Dashboard
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div class="max-w-4xl mx-auto">
      {/* Page Header */}
      <div class="mb-6">
        <h1 class="text-3xl font-bold text-gray-900">Mi Perfil</h1>
        <p class="mt-2 text-sm text-gray-600">
          Gestiona tu información de instructor y mantén tu perfil actualizado.
        </p>
      </div>

      {/* Tabs */}
      <div>
        <div class="border-b border-gray-200 bg-white rounded-t-lg">
          <nav class="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('view')}
              class={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'view'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div class="flex items-center">
                <svg class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Ver Perfil
              </div>
            </button>

            <button
              onClick={() => setActiveTab('edit')}
              class={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'edit'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div class="flex items-center">
                <svg class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Editar Perfil
              </div>
            </button>
          </nav>
        </div>

        {/* Tab content */}
        <div class="mt-6 pb-12">
          {activeTab === 'view' && <ProfileView />}
          {activeTab === 'edit' && <ProfileEditor />}
        </div>
      </div>
    </div>
  );
}

/**
 * Profile View Component
 *
 * Shows a preview of how the instructor's profile appears to others
 * This is a placeholder for future implementation (Milestone 4)
 */
function ProfileView() {
  return (
    <div class="bg-white rounded-lg shadow p-8">
      <div class="text-center py-12">
        <svg class="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        <h3 class="mt-4 text-lg font-medium text-gray-900">Vista Previa del Perfil</h3>
        <p class="mt-2 text-sm text-gray-600 max-w-md mx-auto">
          La vista previa de tu perfil público estará disponible próximamente.
          Por ahora, puedes editar tu información en la pestaña "Editar Perfil".
        </p>
        <div class="mt-6">
          <button
            onClick={() => {}}
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Volver a Editar
          </button>
        </div>

        {/* Info box */}
        <div class="mt-8 p-4 bg-blue-50 border-l-4 border-blue-400 text-left max-w-md mx-auto">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-blue-700">
                <strong>Nota:</strong> Tu perfil será visible para otros instructores activos en el directorio una vez que tu información esté completa y verificada.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
